import { useEffect, useRef } from "react"
import { Link, usePage } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { TicketThread } from "@/components/domain/ticket-thread"
import { TicketComposer } from "@/components/domain/ticket-composer"
import { TicketMetaPanel } from "@/components/domain/ticket-meta-panel"
import type { Ticket } from "@/types/ticket"
import type { SharedProps } from "@/types/inertia"

type Props = {
  ticket: Ticket
  ticket_stream: string
}

export default function TicketsShow({ ticket, ticket_stream }: Props) {
  const { props } = usePage<SharedProps>()
  const flash = props.flash

  // Live thread + status updates. New messages from the customer or AI
  // flow in via AgentMessage's broadcasts_to; status flips (resolved /
  // re-escalated) flow in via AgentConvo's broadcasts_to. Same stream.
  useInertiaCable(ticket_stream, {
    only: ["ticket"],
    debounce: 250,
  })

  // Auto-scroll the thread to the bottom on first paint AND whenever the
  // message count changes (new message arrived). preserveScroll on the
  // composer keeps the user's view stable when THEY send.
  const threadRef = useRef<HTMLDivElement | null>(null)
  const messageCount = ticket.messages?.length ?? 0
  useEffect(() => {
    const el = threadRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messageCount])

  return (
    <AppShell>
      <PageHeader
        eyebrow={<Link href="/tickets" className="hover:text-app-text">Tickets</Link>}
        title={ticket.subject}
        subtitle={`Ticket #${ticket.id} · ${ticket.assignedAgent.name}`}
      />

      {flash.notice && (
        <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
          {flash.alert}
        </div>
      )}

      <section className="flex h-[calc(100vh-220px)] gap-0 overflow-hidden rounded-card border border-border bg-app-panel">
        <div className="flex w-1/2 flex-col">
          <div ref={threadRef} className="flex-1 overflow-y-auto">
            <TicketThread messages={ticket.messages ?? []} />
          </div>
          <TicketComposer
            ticketId={ticket.id}
            disabled={ticket.status === "closed" || ticket.status === "pending_feedback"}
          />
        </div>
        <TicketMetaPanel ticket={ticket} />
      </section>
    </AppShell>
  )
}
